import { Button, HStack, StackProps, useRadio, useRadioGroup, UseRadioProps } from '@chakra-ui/react';

export type TabButtonOption = {
  label: string;
  value: string;
  isDisabled?: boolean;
};

type TabButtonsProps = Omit<StackProps, 'onChange'> & {
  name?: string;
  options: readonly TabButtonOption[];
  value?: string;
  defaultValue?: string;
  onChange?: (nextValue: string) => void;
};

type TabButtonItemProps = UseRadioProps & {
  label: string;
  showDivider: boolean;
};

const TabButtonItem = ({ label, showDivider, ...props }: TabButtonItemProps) => {
  const { getInputProps, getRadioProps, getLabelProps, state } = useRadio(props);
  const inputProps = getInputProps();
  const radioProps = getRadioProps();
  const labelProps = getLabelProps();

  return (
    <Button
      as="label"
      {...labelProps}
      {...radioProps}
      variant="ghost"
      m={0}
      borderRadius={0}
      height="54px"
      fontSize="lg"
      fontWeight="medium"
      px={8}
      color={state.isChecked ? 'primary.600' : 'text.default'}
      borderBottomWidth="2px"
      borderBottomColor={state.isChecked ? 'primary.600' : 'gray.300'}
      borderRightWidth={showDivider ? '1px' : '0px'}
      borderRightColor={showDivider ? 'border.default' : 'transparent'}
      cursor="pointer"
      _hover={{ bg: 'transparent' }}
      _active={{ bg: 'transparent' }}
      _focusVisible={{
        boxShadow: 'none',
        outline: '2px solid',
        outlineColor: 'border.focused',
        outlineOffset: '-2px',
      }}
    >
      <input {...inputProps} hidden />
      {label}
    </Button>
  );
};

export const TabButtons = ({ name, options, value, defaultValue, onChange, ...rest }: TabButtonsProps) => {
  const { getRootProps, getRadioProps } = useRadioGroup({
    name,
    value,
    defaultValue,
    onChange,
  });

  return (
    <HStack
      {...getRootProps()}
      {...rest}
      spacing={0}
      align="stretch"
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="md"
      overflow="hidden"
    >
      {options.map((option, index) => {
        const radioProps = getRadioProps({
          value: option.value,
          isDisabled: option.isDisabled,
        });

        return (
          <TabButtonItem
            key={option.value}
            label={option.label}
            showDivider={index < options.length - 1}
            {...radioProps}
          />
        );
      })}
    </HStack>
  );
};
